import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Trash2, EyeOff, X } from 'lucide-react';

interface DeleteRowDialogProps {
  isOpen: boolean;
  rowKey: string;
  onClose: () => void;
  onPermanent: () => void;
  onTemporary: () => void;
}

export const DeleteRowDialog: React.FC<DeleteRowDialogProps> = ({
  isOpen,
  rowKey,
  onClose,
  onPermanent,
  onTemporary
}) => {
  if (!isOpen) return null;

  const dateFromKey = rowKey.includes('_') ? rowKey.split('_')[0] : rowKey;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          >
            {/* Dialog */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Remove Row
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <p className="text-gray-700 mb-6">
                How would you like to remove the row for <span className="font-semibold">{dateFromKey}</span>?
              </p>

              {/* Options */}
              <div className="space-y-3">
                {/* Permanent Delete */}
                <button
                  onClick={() => {
                    onPermanent();
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 p-4 border-2 border-red-200 hover:border-red-400 rounded-lg transition-colors group"
                >
                  <div className="p-2 bg-red-50 group-hover:bg-red-100 rounded-lg transition-colors">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-gray-900">Permanent Delete</div>
                    <div className="text-sm text-gray-600">
                      Delete from database. Cannot be undone.
                    </div>
                  </div>
                </button>

                {/* Temporary Hide */}
                <button
                  onClick={() => {
                    onTemporary();
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 p-4 border-2 border-[#C2DFF1] hover:border-[#3EA3DA] rounded-lg transition-colors group"
                >
                  <div className="p-2 bg-[#F2F8FC] group-hover:bg-[#E3F0F9] rounded-lg transition-colors">
                    <EyeOff className="w-5 h-5 text-[#0066A3]" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-gray-900">Temporary Hide</div>
                    <div className="text-sm text-gray-600">
                      Hide until page refresh. Data remains in database.
                    </div>
                  </div>
                </button>
              </div>

              {/* Cancel */}
              <button
                onClick={onClose}
                className="w-full mt-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

